/**
 * The shell every legal page sits in: a title band, the effective date, and a
 * readable measure for long prose. The three pages are linked to each other in
 * the footer because a reader who lands on one of them is usually looking for
 * one of the others.
 */

import { ArrowLeft } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { SEO } from "@/components/SEO";

import { formatLegalDate, isLegalIdentityComplete } from "./legalDetails";

interface LegalPageLayoutProps {
  title: string;
  subtitle: string;
  description: string;
  path: string;
  children: ReactNode;
}

const LEGAL_LINKS = [
  { to: "/privacy", label: "Privacy Policy" },
  { to: "/terms", label: "Terms of Service" },
  { to: "/imprint", label: "Imprint" },
  { to: "/security", label: "Security" },
  { to: "/contact", label: "Contact" },
];

export function LegalPageLayout({
  title,
  subtitle,
  description,
  path,
  children,
}: LegalPageLayoutProps) {
  const url = `https://travelhealthpass.app${path}`;

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO title={title} description={description} url={url} canonical={url} />

      {/* pt-28, not py-20: the global OfflineBanner is fixed at top-16 and
          would otherwise cover this heading. Same reason as on the FAQ page. */}
      <div className="bg-primary-600 text-white pb-16 pt-28">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-primary-100 hover:text-white transition mb-6 text-sm"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to HealthPass
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold mb-3">{title}</h1>
          <p className="text-lg text-primary-100">{subtitle}</p>
          <p className="mt-4 text-sm text-primary-200">
            In effect since {formatLegalDate()}
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {!isLegalIdentityComplete() && (
          <div
            role="alert"
            className="mb-8 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"
          >
            <strong className="font-semibold">Draft — not yet in force.</strong>{" "}
            The operator&rsquo;s company details have still to be entered in{" "}
            <code>frontend/src/pages/legal/legalDetails.ts</code>. This document
            must not be relied on until they are.
          </div>
        )}

        <div className="space-y-8 text-gray-700 leading-relaxed">
          {children}
        </div>

        <nav className="mt-12 border-t border-gray-200 pt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm">
          {LEGAL_LINKS.filter((link) => link.to !== path).map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="text-primary-600 hover:underline"
            >
              {link.label}
            </Link>
          ))}
        </nav>
      </div>
    </div>
  );
}

/** A numbered section. The numbering is what people cite when they write in. */
export function LegalSection({
  number,
  heading,
  children,
}: {
  number: number;
  heading: string;
  children: ReactNode;
}) {
  return (
    <section id={`section-${number}`} className="scroll-mt-24">
      <h2 className="text-xl font-semibold text-gray-900 mb-3">
        {number}. {heading}
      </h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

/** The bulleted lists both long documents are mostly made of. */
export function LegalList({ items }: { items: ReactNode[] }) {
  return (
    <ul className="list-disc pl-6 space-y-2">
      {items.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ul>
  );
}
